import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Archive, Key, Pencil, Plus, RefreshCw, Search, Upload, UserPlus, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDate, formatRelative } from "@/lib/dates";

const ROLES = ["employee", "manager", "campus_pastor", "po_admin", "super_admin", "volunteer"];
const STATUSES = ["active", "on_leave", "separated"];

const emptyForm = {
  name: "",
  email: "",
  password: "",
  campus_id: "",
  department: "",
  job_title: "",
  manager_id: "",
  hire_date: "",
  employment_status: "active",
  role: "employee",
  phone: "",
};

export default function UserManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [managerQuery, setManagerQuery] = useState("");
  const [managerOpen, setManagerOpen] = useState(false);

  const isSuperAdmin = me?.role === "super_admin";

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users", {
        params: { include_archived: includeArchived, q: q || undefined },
      });
      setUsers(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/campuses");
        setCampuses(data);
      } catch (err) {
        console.error("campus dropdown load failed", err);
        toast.error("Couldn't load campus list.");
      }
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(loadUsers, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, includeArchived]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError("");
    setManagerQuery("");
    setDrawerOpen(true);
  };

  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({
      name: u.name || "",
      email: u.email || "",
      password: "",
      campus_id: u.campus_id || "",
      department: u.department || "",
      job_title: u.job_title || "",
      manager_id: u.manager_id || "",
      hire_date: u.hire_date || "",
      employment_status: u.employment_status || "active",
      role: u.role || "employee",
      phone: u.phone || "",
    });
    const mgr = users.find((x) => x.id === u.manager_id);
    setManagerQuery(mgr?.name || "");
    setError("");
    setDrawerOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editingId) {
        const payload = { ...form };
        delete payload.email;
        delete payload.password;
        if (!payload.manager_id) payload.manager_id = null;
        if (!payload.campus_id) payload.campus_id = null;
        await api.patch(`/users/${editingId}`, payload);
        toast.success("Team member updated.");
      } else {
        const payload = { ...form };
        if (!payload.manager_id) delete payload.manager_id;
        if (!payload.campus_id) delete payload.campus_id;
        await api.post("/users", payload);
        toast.success("Team member added. Welcome email logged.");
      }
      setDrawerOpen(false);
      loadUsers();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this team member? They will lose access, but their records remain.")) return;
    try {
      await api.post(`/users/${id}/archive`);
      toast.success("Team member archived.");
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const restore = async (id) => {
    try {
      await api.post(`/users/${id}/restore`);
      toast.success("Team member restored.");
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  // -----------------------------------------------------------------------
  // Admin password reset (single) + bulk credentials generation
  // -----------------------------------------------------------------------
  const [tempCredentials, setTempCredentials] = useState(null);
  // shape: { rows: [{name,email,temporary_password,...}] } or single row

  const [selected, setSelected] = useState(new Set());
  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelected = () => setSelected(new Set());

  const resetPassword = async (u) => {
    if (!window.confirm(
      `Reset password for ${u.name}? A temporary password will be shown to you once — copy and share securely.`,
    )) return;
    try {
      const { data } = await api.post(`/auth/admin/users/${u.id}/reset-password`);
      setTempCredentials({ rows: [data] });
      toast.success("Temporary password generated.");
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const bulkGenerate = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one team member.");
      return;
    }
    if (!window.confirm(
      `Generate temporary passwords for ${selected.size} team member(s)? Each password is shown to you once.`,
    )) return;
    try {
      const { data } = await api.post("/auth/admin/users/bulk-credentials", {
        user_ids: Array.from(selected),
      });
      setTempCredentials(data);
      clearSelected();
      loadUsers();
      toast.success(`Generated ${data.count} temporary password${data.count === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const downloadCredentialsCsv = () => {
    if (!tempCredentials?.rows?.length) return;
    const header = "Name,Email,Temporary Password,Role,Campus\n";
    const lines = tempCredentials.rows.map((r) =>
      [r.name || "", r.email, r.temporary_password, r.role || "", r.campus_name || ""]
        .map((x) => `"${String(x).replaceAll('"', '""')}"`).join(","),
    );
    const csv = header + lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mypatria-credentials-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const managerOptions = useMemo(
    () =>
      users
        .filter((u) => !u.archived)
        .filter((u) =>
          managerQuery
            ? (u.name + " " + u.email).toLowerCase().includes(managerQuery.toLowerCase())
            : true
        )
        .slice(0, 8),
    [users, managerQuery]
  );

  const campusName = (id) => campuses.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
            People Operations
          </p>
          <h1 className="text-4xl font-serif text-forest-900 mt-1">People Management</h1>
          <p className="mt-2 text-sm text-charcoal-soft">
            Add teammates, adjust roles, and archive with dignity — no hard deletes.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Link
            to="/people/manage/import"
            className="patria-btn-secondary"
            data-testid="btn-import-users"
          >
            <Upload className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            Import Employees
          </Link>
          <button className="patria-btn-primary" onClick={openNew} data-testid="btn-new-user">
            <UserPlus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Add Team Member
          </button>
        </div>
      </div>

      <div className="patria-card p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal-muted"
            strokeWidth={1.5}
          />
          <input
            className="patria-input w-full pl-9"
            placeholder="Search by name or email..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="user-search"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-charcoal-soft">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            data-testid="include-archived"
            className="rounded"
          />
          Include archived
        </label>
        {selected.size > 0 && (
          <button
            type="button"
            onClick={bulkGenerate}
            className="patria-btn-primary text-sm"
            data-testid="btn-bulk-credentials"
          >
            <Users className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            Generate Login Credentials ({selected.size})
          </button>
        )}
      </div>

      <div className="overflow-hidden rounded-2xl shadow-card">
        <table className="patria-table w-full">
          <thead>
            <tr>
              <th className="w-8"></th>
              <th>Name</th>
              <th>Email</th>
              <th>Campus</th>
              <th>Department</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="italic text-charcoal-muted">
                  Loading team...
                </td>
              </tr>
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={9} className="italic text-charcoal-muted">
                  No teammates match those filters.
                </td>
              </tr>
            )}
            {!loading &&
              users.map((u) => (
                <tr key={u.id} data-testid={`um-row-${u.id}`} className={u.archived ? "opacity-70" : ""}>
                  <td>
                    {!u.archived && (
                      <input
                        type="checkbox"
                        checked={selected.has(u.id)}
                        onChange={() => toggleSelected(u.id)}
                        className="rounded"
                        data-testid={`um-select-${u.id}`}
                        aria-label={`Select ${u.name}`}
                      />
                    )}
                  </td>
                  <td className="font-medium text-forest-900">
                    {u.name}
                    {u.archived && (
                      <span className="ml-2 patria-chip-terra">Archived</span>
                    )}
                    {u.temp_password && (
                      <span className="ml-2 patria-chip-terra" data-testid={`um-tempflag-${u.id}`}>Temp password</span>
                    )}
                  </td>
                  <td className="text-charcoal-soft">{u.email}</td>
                  <td>{campusName(u.campus_id)}</td>
                  <td>{u.department || "—"}</td>
                  <td>
                    <span className="patria-chip-green capitalize">
                      {u.role.replace("_", " ")}
                    </span>
                  </td>
                  <td className="capitalize">{(u.employment_status || "").replace("_", " ")}</td>
                  <td className="text-xs text-charcoal-muted" data-testid={`um-lastlogin-${u.id}`}>
                    {u.last_login_at
                      ? <span title={formatDate(u.last_login_at, { withTime: true })}>{formatRelative(u.last_login_at)}</span>
                      : "Never"}
                  </td>
                  <td className="text-right space-x-2 whitespace-nowrap">
                    <button
                      className="patria-btn-ghost"
                      onClick={() => openEdit(u)}
                      data-testid={`um-edit-${u.id}`}
                    >
                      <Pencil className="h-4 w-4" strokeWidth={1.5} /> Edit
                    </button>
                    {!u.archived && (
                      <button
                        className="patria-btn-ghost text-forest-700"
                        onClick={() => resetPassword(u)}
                        data-testid={`um-reset-pw-${u.id}`}
                      >
                        <Key className="h-4 w-4" strokeWidth={1.5} /> Reset Password
                      </button>
                    )}
                    {u.archived ? (
                      <button
                        className="patria-btn-ghost text-forest-700"
                        onClick={() => restore(u.id)}
                        data-testid={`um-restore-${u.id}`}
                      >
                        <RefreshCw className="h-4 w-4" strokeWidth={1.5} /> Restore
                      </button>
                    ) : (
                      <button
                        className="patria-btn-ghost text-terracotta-600"
                        onClick={() => archive(u.id)}
                        data-testid={`um-archive-${u.id}`}
                      >
                        <Archive className="h-4 w-4" strokeWidth={1.5} /> Archive
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {tempCredentials && (
        <CredentialsModal
          data={tempCredentials}
          onClose={() => setTempCredentials(null)}
          onDownload={downloadCredentialsCsv}
        />
      )}

      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex" data-testid="user-drawer">
          <div
            className="absolute inset-0 bg-forest-900/40 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <form
            onSubmit={submit}
            className="relative ml-auto h-full w-full max-w-lg bg-cream-100 shadow-card overflow-y-auto p-6 animate-fade-up"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-serif text-forest-900">
                {editingId ? "Edit team member" : "Add team member"}
              </h2>
              <button
                type="button"
                className="patria-btn-ghost"
                onClick={() => setDrawerOpen(false)}
              >
                Cancel
              </button>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <input
                  className="patria-input w-full"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="uf-name"
                />
              </Field>
              <Field label="Work email" required>
                <input
                  type="email"
                  className="patria-input w-full"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  disabled={!!editingId}
                  data-testid="uf-email"
                />
              </Field>
              {!editingId && (
                <Field label="Initial password" hint="Min 8 chars, upper + lower + number">
                  <input
                    type="text"
                    className="patria-input w-full"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                    data-testid="uf-password"
                  />
                </Field>
              )}
              <Field label="Role">
                <select
                  className="patria-input w-full"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="uf-role"
                >
                  {ROLES.map((r) => (
                    <option
                      key={r}
                      value={r}
                      disabled={r === "super_admin" && !isSuperAdmin}
                    >
                      {r.replace("_", " ")}
                      {r === "super_admin" && !isSuperAdmin ? " (Super Admin only)" : ""}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Campus">
                <select
                  className="patria-input w-full"
                  value={form.campus_id}
                  onChange={(e) => setForm({ ...form, campus_id: e.target.value })}
                  data-testid="uf-campus"
                >
                  <option value="">Unassigned</option>
                  {campuses.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Department">
                <input
                  className="patria-input w-full"
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  data-testid="uf-department"
                />
              </Field>
              <Field label="Job title">
                <input
                  className="patria-input w-full"
                  value={form.job_title}
                  onChange={(e) => setForm({ ...form, job_title: e.target.value })}
                  data-testid="uf-title"
                />
              </Field>
              <Field label="Hire date">
                <input
                  type="date"
                  className="patria-input w-full"
                  value={form.hire_date || ""}
                  onChange={(e) => setForm({ ...form, hire_date: e.target.value })}
                  data-testid="uf-hire"
                />
              </Field>
              <Field label="Employment status">
                <select
                  className="patria-input w-full"
                  value={form.employment_status}
                  onChange={(e) => setForm({ ...form, employment_status: e.target.value })}
                  data-testid="uf-status"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s.replace("_", " ")}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Phone">
                <input
                  className="patria-input w-full"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="uf-phone"
                />
              </Field>

              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1.5">
                  Manager
                </label>
                <div className="relative">
                  <input
                    className="patria-input w-full"
                    value={managerQuery}
                    placeholder="Search a teammate to assign as manager..."
                    onChange={(e) => {
                      setManagerQuery(e.target.value);
                      setManagerOpen(true);
                    }}
                    onFocus={() => setManagerOpen(true)}
                    onBlur={() => setTimeout(() => setManagerOpen(false), 120)}
                    data-testid="uf-manager"
                  />
                  {managerOpen && (
                    <ul className="absolute z-10 mt-1 w-full patria-card p-1 max-h-56 overflow-y-auto">
                      <li
                        className="px-3 py-2 rounded hover:bg-sage-200/70 cursor-pointer text-sm italic text-charcoal-muted"
                        onMouseDown={() => {
                          setForm({ ...form, manager_id: "" });
                          setManagerQuery("");
                        }}
                      >
                        No manager
                      </li>
                      {managerOptions.map((u) => (
                        <li
                          key={u.id}
                          className="px-3 py-2 rounded hover:bg-sage-200/70 cursor-pointer text-sm"
                          onMouseDown={() => {
                            setForm({ ...form, manager_id: u.id });
                            setManagerQuery(u.name);
                          }}
                        >
                          <p className="font-medium text-forest-900">{u.name}</p>
                          <p className="text-xs text-charcoal-muted">{u.email}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            {error && (
              <p className="mt-4 rounded-lg bg-terracotta-500/10 border border-terracotta-500/30 text-terracotta-700 px-3 py-2 text-sm" data-testid="uf-error">
                {error}
              </p>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" className="patria-btn-secondary" onClick={() => setDrawerOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="patria-btn-primary" disabled={saving} data-testid="uf-submit">
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                {saving ? "Saving..." : editingId ? "Save changes" : "Add team member"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1.5">
        {label} {required && <span className="text-terracotta-500">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-charcoal-muted italic">{hint}</p>}
    </div>
  );
}


function CredentialsModal({ data, onClose, onDownload }) {
  const rows = data?.rows || [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-forest-900/50 p-4" data-testid="credentials-modal">
      <div className="patria-card p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
          Temporary Credentials
        </p>
        <h2 className="font-serif text-2xl text-forest-900 mt-1">
          Copy These Passwords Now — Shown Once
        </h2>
        <p className="text-sm text-charcoal-soft mt-2">
          These temporary passwords are visible only in this window. Share them securely
          with each teammate — they can be nudged to change theirs the next time they sign in.
        </p>
        <div className="mt-4 rounded-xl bg-cream-100 max-h-[45vh] overflow-y-auto">
          <table className="patria-table w-full text-sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Temporary Password</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.email} data-testid={`cred-row-${r.email}`}>
                  <td className="text-forest-900">{r.name}</td>
                  <td className="text-charcoal-soft">{r.email}</td>
                  <td className="font-mono">{r.temporary_password}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onDownload}
            className="patria-btn-secondary"
            data-testid="btn-download-credentials"
          >
            Download CSV
          </button>
          <button
            type="button"
            onClick={onClose}
            className="patria-btn-primary"
            data-testid="btn-close-credentials"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
